import React, { useEffect, useMemo, useState } from 'react';
import { signOut } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { saveAttendance, listAttendance } from '../api/attendanceApi';
import { saveSubjectMark, listAllMarks, listMarksForStudent } from '../api/marksApi';
import { listStudentsByClass } from '../api/studentsApi';
import {
  listAssessments,
  listSyllabusForClass,
  saveSyllabus,
  saveAssessmentMark,
  listAssessmentMarksForStudent,
  listSubjects,
  subjectsForClass,
  assessmentNamesForClass,
  findAssessment,
} from '../api/academicsApi';
import { AppHeader } from './LoginPanel';
import SearchableSelect from './SearchableSelect';
import { teacherClassOptions, formatClassLabel } from '../constants/classes';
import { matchesSearch } from '../utils/searchFilter';

export default function TeacherPanel() {
  const { user, profile } = useAuth();
  const [tab, setTab] = useState('attendance');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [marksRows, setMarksRows] = useState([]);

  const today = new Date().toISOString().slice(0, 10);
  /** Teacher must pick a class (Nur–8) before the student list loads. */
  const [attForm, setAttForm] = useState({ date: today, className: '' });
  const [studentsInClass, setStudentsInClass] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  /** studentId -> checked = present, unchecked = absent (default present). */
  const [presence, setPresence] = useState({});
  const [attStudentFilter, setAttStudentFilter] = useState('');
  const [attTableFilter, setAttTableFilter] = useState('');
  const [marksTableFilter, setMarksTableFilter] = useState('');

  const [syllabusForm, setSyllabusForm] = useState({ className: '', subject: '', content: '' });
  const [syllabusRows, setSyllabusRows] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [marksEntry, setMarksEntry] = useState({ className: '', studentId: '', assessmentName: '' });
  const [marksStudents, setMarksStudents] = useState([]);
  const [marksStudentsLoading, setMarksStudentsLoading] = useState(false);
  const [subjectScores, setSubjectScores] = useState({});
  const [savingMarks, setSavingMarks] = useState(false);

  const classOpts = useMemo(() => teacherClassOptions(profile), [profile]);
  const classOptsWithEmpty = useMemo(
    () => [{ value: '', label: 'Select class (NUR–8)…' }, ...classOpts],
    [classOpts]
  );
  const classSubjects = useMemo(
    () => subjectsForClass(subjects, marksEntry.className),
    [subjects, marksEntry.className]
  );

  const assessmentNameOpts = useMemo(
    () => assessmentNamesForClass(assessments, marksEntry.className).map((n) => ({ value: n, label: n })),
    [assessments, marksEntry.className]
  );

  const marksStudentOpts = useMemo(
    () =>
      marksStudents.map((s) => ({
        value: s.studentId,
        label: `${s.studentName} (${s.studentId})`,
      })),
    [marksStudents]
  );

  const selectedMarksStudent = useMemo(
    () => marksStudents.find((s) => s.studentId === marksEntry.studentId) || null,
    [marksStudents, marksEntry.studentId]
  );

  const subjectOpts = useMemo(
    () => subjects.map((s) => ({ value: s.name, label: s.class ? `${s.name} (${formatClassLabel(s.class)})` : s.name })),
    [subjects]
  );

  const filteredStudentsForAtt = useMemo(
    () => studentsInClass.filter((s) => matchesSearch(attStudentFilter, [s.studentName, s.studentId])),
    [studentsInClass, attStudentFilter]
  );

  const filteredMarksRows = useMemo(
    () =>
      marksRows.filter((m) =>
        matchesSearch(marksTableFilter, [m.studentName, m.id, m.class, m.subject, String(m.score ?? '')])
      ),
    [marksRows, marksTableFilter]
  );

  const filteredAttRows = useMemo(
    () =>
      attendanceRows.filter((r) =>
        matchesSearch(attTableFilter, [r.date, r.class, (r.presentStudents || []).join(', ')])
      ),
    [attendanceRows, attTableFilter]
  );

  const refreshAttendance = async () => {
    const att = await listAttendance();
    setAttendanceRows(att.sort((a, b) => (b.date || '').localeCompare(a.date || '')));
  };

  useEffect(() => {
    async function load() {
      if (!db) return;
      const [, marks] = await Promise.all([refreshAttendance(), listAllMarks()]);
      setMarksRows(marks);
    }
    load().catch((e) => setErr(e.message));
  }, [msg]);

  useEffect(() => {
    if (!db || tab !== 'attendance') return;
    const className = String(attForm.className || '').trim();
    if (!className) {
      setStudentsInClass([]);
      setPresence({});
      setStudentsLoading(false);
      return;
    }
    let cancelled = false;
    setStudentsLoading(true);
    setErr('');
    (async () => {
      try {
        const list = await listStudentsByClass(className);
        if (cancelled) return;
        setStudentsInClass(list);
        setPresence(Object.fromEntries(list.map((s) => [s.studentId, true])));
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setStudentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attForm.className, tab]);

  useEffect(() => {
    if (tab !== 'syllabus' && tab !== 'marks') return;
    listSubjects().then(setSubjects).catch(() => setSubjects([]));
    listAssessments().then(setAssessments).catch(() => setAssessments([]));
  }, [tab, msg]);

  useEffect(() => {
    if (tab !== 'marks' || !marksEntry.className) {
      setMarksStudents([]);
      setMarksEntry((m) => ({ ...m, studentId: '' }));
      return undefined;
    }
    let cancelled = false;
    setMarksStudentsLoading(true);
    listStudentsByClass(marksEntry.className)
      .then((list) => {
        if (!cancelled) setMarksStudents(list);
      })
      .catch(() => {
        if (!cancelled) setMarksStudents([]);
      })
      .finally(() => {
        if (!cancelled) setMarksStudentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, marksEntry.className]);

  useEffect(() => {
    if (tab !== 'marks' || !marksEntry.studentId || !marksEntry.className) {
      setSubjectScores({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [legacyMarks, examMarks] = await Promise.all([
          listMarksForStudent(marksEntry.studentId),
          listAssessmentMarksForStudent(marksEntry.studentId, marksEntry.studentId),
        ]);
        if (cancelled) return;
        const scores = {};
        classSubjects.forEach((sub) => {
          const legacy = legacyMarks.find((m) => m.subject === sub.name);
          if (legacy?.score != null && legacy.score !== '') scores[sub.name] = legacy.score;
          if (marksEntry.assessmentName) {
            const ass = findAssessment(
              assessments,
              marksEntry.className,
              marksEntry.assessmentName,
              sub.name
            );
            if (ass) {
              const hit = examMarks.find((m) => m.assessmentId === ass.id);
              if (hit?.score != null && hit.score !== '') scores[sub.name] = hit.score;
            }
          }
        });
        setSubjectScores(scores);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, marksEntry.studentId, marksEntry.className, marksEntry.assessmentName, classSubjects, assessments]);

  useEffect(() => {
    if (tab !== 'syllabus' || !syllabusForm.className) {
      setSyllabusRows([]);
      return undefined;
    }
    let cancelled = false;
    listSyllabusForClass(syllabusForm.className)
      .then((rows) => {
        if (!cancelled) setSyllabusRows(rows);
      })
      .catch(() => {
        if (!cancelled) setSyllabusRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, syllabusForm.className, msg]);

  const handleSyllabusSave = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    try {
      await saveSyllabus({
        className: syllabusForm.className,
        subject: syllabusForm.subject,
        content: syllabusForm.content,
        updatedBy: profile?.email || user?.email,
      });
      setMsg(`Syllabus saved for class ${formatClassLabel(syllabusForm.className)} — ${syllabusForm.subject}`);
      setSyllabusForm((f) => ({ ...f, content: '' }));
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const handleMarksSave = async (e) => {
    e.preventDefault();
    if (!marksEntry.className || !marksEntry.studentId || !selectedMarksStudent) {
      setErr('Please select class and student.');
      return;
    }
    if (!classSubjects.length) {
      setErr('No subjects for this class. Ask admin to add subjects in Academics.');
      return;
    }
    const filled = classSubjects.filter((s) => subjectScores[s.name] !== '' && subjectScores[s.name] != null);
    if (!filled.length) {
      setErr('Enter at least one subject score.');
      return;
    }
    setMsg('');
    setErr('');
    setSavingMarks(true);
    try {
      let count = 0;
      for (const sub of filled) {
        const raw = subjectScores[sub.name];
        await saveSubjectMark({
          studentId: selectedMarksStudent.studentId,
          studentName: selectedMarksStudent.studentName,
          className: marksEntry.className,
          subject: sub.name,
          score: raw,
        });
        if (marksEntry.assessmentName) {
          const ass = findAssessment(assessments, marksEntry.className, marksEntry.assessmentName, sub.name);
          if (ass) {
            await saveAssessmentMark({
              assessmentId: ass.id,
              admno: selectedMarksStudent.studentId,
              studentName: selectedMarksStudent.studentName,
              className: marksEntry.className,
              subject: sub.name,
              score: raw,
              maxMarks: ass.maxMarks,
            });
          }
        }
        count += 1;
      }
      const marks = await listAllMarks();
      setMarksRows(marks);
      setMsg(`Saved marks for ${selectedMarksStudent.studentName} — ${count} subject(s).`);
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSavingMarks(false);
    }
  };

  const handleAttendance = async (e) => {
    e.preventDefault();
    setMsg('');
    setErr('');
    try {
      const presentIds = studentsInClass.filter((s) => presence[s.studentId] !== false).map((s) => s.studentId);
      const absentIds = studentsInClass.filter((s) => presence[s.studentId] === false).map((s) => s.studentId);
      const count = await saveAttendance({ ...attForm, presentStudents: presentIds, absentStudents: absentIds });
      const total = studentsInClass.length;
      setMsg(
        `Attendance saved for class ${attForm.className}: ${count} present, ${total - count} absent (${total} students).`
      );
      setPresence(Object.fromEntries(studentsInClass.map((s) => [s.studentId, true])));
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="mps-app">
      <AppHeader title="Teacher dashboard" user={user} profile={profile} onSignOut={() => auth && signOut(auth)} />
      <main className="mps-main">
        <div className="mps-tabs">
          <button type="button" className={`mps-tab ${tab === 'attendance' ? 'mps-tab-active' : ''}`} onClick={() => setTab('attendance')}>
            Mark attendance
          </button>
          <button type="button" className={`mps-tab ${tab === 'syllabus' ? 'mps-tab-active' : ''}`} onClick={() => setTab('syllabus')}>
            Syllabus
          </button>
          <button type="button" className={`mps-tab ${tab === 'marks' ? 'mps-tab-active' : ''}`} onClick={() => setTab('marks')}>
            Enter marks
          </button>
        </div>

        {err ? <div className="mps-msg mps-msg-error">{err}</div> : null}
        {msg ? <div className="mps-msg mps-msg-success">{msg}</div> : null}

        {tab === 'attendance' ? (
          <>
            <div className="mps-card">
              <h2>Mark attendance</h2>
              <p className="mps-muted" style={{ marginTop: 0 }}>
                Select <strong>class (NUR–8)</strong> first. All students start <strong>checked (present)</strong> — uncheck for absent, then save. Re-saving on the same date updates that class record.
              </p>
              <form onSubmit={handleAttendance}>
                <div className="mps-grid">
                  <div className="mps-field">
                    <label>Date</label>
                    <input type="date" value={attForm.date} onChange={(e) => setAttForm({ ...attForm, date: e.target.value })} required />
                  </div>
                  <div className="mps-field">
                    <label htmlFor="att-class-select">Class (required)</label>
                    <SearchableSelect
                      id="att-class-select"
                      value={attForm.className}
                      onChange={(v) => setAttForm({ ...attForm, className: v })}
                      options={classOptsWithEmpty}
                      placeholder="Type to search NUR, L.K.G, 1…"
                    />
                  </div>
                </div>
                {!attForm.className ? (
                  <p className="mps-msg mps-msg-info" style={{ marginTop: 16 }}>
                    Select a class above to load the student list.
                  </p>
                ) : null}
                {attForm.className ? (
                  <div className="mps-field" style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <label>
                        Students — class <strong>{attForm.className}</strong>
                      </label>
                      {studentsInClass.length > 0 ? (
                        <span className="mps-att-bulk-actions">
                          <button
                            type="button"
                            className="mps-btn mps-btn-secondary mps-btn-sm"
                            onClick={() =>
                              setPresence((prev) => {
                                const next = { ...prev };
                                studentsInClass.forEach((s) => {
                                  next[s.studentId] = true;
                                });
                                return next;
                              })
                            }
                          >
                            Check all (present)
                          </button>
                          <button
                            type="button"
                            className="mps-btn mps-btn-secondary mps-btn-sm"
                            onClick={() =>
                              setPresence((prev) => {
                                const next = { ...prev };
                                studentsInClass.forEach((s) => {
                                  next[s.studentId] = false;
                                });
                                return next;
                              })
                            }
                          >
                            Uncheck all (absent)
                          </button>
                        </span>
                      ) : null}
                    </div>
                    {studentsInClass.length > 0 ? (
                      <input
                        type="search"
                        className="mps-table-filter"
                        style={{ maxWidth: '100%', marginTop: 8 }}
                        value={attStudentFilter}
                        onChange={(e) => setAttStudentFilter(e.target.value)}
                        placeholder="Filter students by name or ID…"
                        aria-label="Filter students"
                      />
                    ) : null}
                    {studentsLoading ? (
                      <p className="mps-muted" style={{ margin: '8px 0 0' }}>
                        Loading students…
                      </p>
                    ) : studentsInClass.length === 0 ? (
                      <p className="mps-muted" style={{ margin: '8px 0 0' }}>
                        No students found for class <strong>{attForm.className}</strong>. If this class should have students, ask admin to
                        refresh the school registry or run the Firebase student import.
                      </p>
                    ) : filteredStudentsForAtt.length === 0 ? (
                      <p className="mps-muted" style={{ margin: '8px 0 0' }}>
                        No students match your search.
                      </p>
                    ) : (
                      <ul className="mps-attendance-list">
                        {filteredStudentsForAtt.map((s) => {
                          const inputId = `att-${s.studentId}`;
                          const isPresent = presence[s.studentId] !== false;
                          return (
                            <li key={s.studentId} className="mps-att-row">
                              <input
                                id={inputId}
                                type="checkbox"
                                className="mps-att-cb"
                                checked={isPresent}
                                onChange={(e) =>
                                  setPresence((prev) => ({ ...prev, [s.studentId]: e.target.checked }))
                                }
                                aria-label={`Present: ${s.studentName}`}
                              />
                              <label htmlFor={inputId} className="mps-att-row-name">
                                {s.studentName} <span className="mps-muted">({s.studentId})</span>
                              </label>
                              <span className={`mps-att-status ${isPresent ? 'mps-att-status-present' : 'mps-att-status-absent'}`}>
                                {isPresent ? 'Present' : 'Absent'}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ) : null}
                <button
                  type="submit"
                  className="mps-btn"
                  style={{ marginTop: 16 }}
                  disabled={!attForm.className || studentsInClass.length === 0}
                >
                  Save attendance
                </button>
              </form>
            </div>
            <div className="mps-card">
              <h2>Recent attendance</h2>
              <input
                type="search"
                className="mps-table-filter"
                value={attTableFilter}
                onChange={(e) => setAttTableFilter(e.target.value)}
                placeholder="Search date, class, student IDs…"
                aria-label="Filter attendance table"
              />
              <div className="mps-table-wrap mps-att-history-wrap">
              <table className="mps-table mps-att-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Class</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th className="mps-att-history-hide-mobile">Student IDs (present)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="mps-muted">
                        No attendance records yet.
                      </td>
                    </tr>
                  ) : (
                    filteredAttRows.slice(0, 20).map((row) => (
                      <tr key={row.id}>
                        <td data-label="Date">{row.date}</td>
                        <td data-label="Class">{row.class}</td>
                        <td data-label="Present">{(row.presentStudents || []).length}</td>
                        <td data-label="Absent">{(row.absentStudents || []).length}</td>
                        <td data-label="Present IDs" className="mps-att-history-hide-mobile">
                          {(row.presentStudents || []).join(', ') || '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </>
        ) : tab === 'syllabus' ? (
          <>
            <div className="mps-card">
              <h2>Class-wise syllabus</h2>
              <form onSubmit={handleSyllabusSave}>
                <div className="mps-grid">
                  <div className="mps-field">
                    <label>Class</label>
                    <SearchableSelect
                      value={syllabusForm.className}
                      onChange={(v) => setSyllabusForm({ ...syllabusForm, className: v })}
                      options={classOptsWithEmpty}
                      placeholder="Select class…"
                    />
                  </div>
                  <div className="mps-field">
                    <label>Subject</label>
                    {subjectOpts.length > 0 ? (
                      <SearchableSelect
                        value={syllabusForm.subject}
                        onChange={(v) => setSyllabusForm({ ...syllabusForm, subject: v })}
                        options={subjectOpts}
                        placeholder="Pick or type subject…"
                      />
                    ) : (
                      <input
                        value={syllabusForm.subject}
                        onChange={(e) => setSyllabusForm({ ...syllabusForm, subject: e.target.value })}
                        required
                      />
                    )}
                  </div>
                </div>
                <div className="mps-field" style={{ marginTop: 12 }}>
                  <label>Syllabus content</label>
                  <textarea
                    rows={8}
                    value={syllabusForm.content}
                    onChange={(e) => setSyllabusForm({ ...syllabusForm, content: e.target.value })}
                    required
                    placeholder="Units, chapters, topics…"
                  />
                </div>
                <button type="submit" className="mps-btn" style={{ marginTop: 12 }} disabled={!syllabusForm.className}>
                  Save syllabus
                </button>
              </form>
            </div>
            {syllabusRows.length > 0 ? (
              <div className="mps-card">
                <h2>Saved — class {formatClassLabel(syllabusForm.className)}</h2>
                <ul className="mps-syllabus-list">
                  {syllabusRows.map((row) => (
                    <li key={row.id}>
                      <strong>{row.subject}</strong>
                      <pre className="mps-syllabus-preview">{row.content}</pre>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : tab === 'marks' ? (
          <>
            <div className="mps-card">
              <h2>Enter marks</h2>
              <p className="mps-muted" style={{ marginTop: 0 }}>
                Select <strong>class</strong>, then <strong>student</strong>. Enter scores for all subjects of that class. Optionally link to an exam/test created by admin.
              </p>
              <form onSubmit={handleMarksSave}>
                <div className="mps-grid">
                  <div className="mps-field">
                    <label>Class</label>
                    <SearchableSelect
                      value={marksEntry.className}
                      onChange={(v) =>
                        setMarksEntry({ className: v, studentId: '', assessmentName: marksEntry.assessmentName })
                      }
                      options={classOptsWithEmpty}
                      placeholder="Select class…"
                    />
                  </div>
                  <div className="mps-field">
                    <label>Student</label>
                    <SearchableSelect
                      value={marksEntry.studentId}
                      onChange={(v) => setMarksEntry((m) => ({ ...m, studentId: v }))}
                      options={marksStudentOpts}
                      emptyOption={{ value: '', label: '— Select student —' }}
                      placeholder={marksStudentsLoading ? 'Loading students…' : 'Search student…'}
                    />
                  </div>
                  {assessmentNameOpts.length > 0 ? (
                    <div className="mps-field">
                      <label>Exam / test (optional)</label>
                      <SearchableSelect
                        value={marksEntry.assessmentName}
                        onChange={(v) => setMarksEntry((m) => ({ ...m, assessmentName: v }))}
                        options={assessmentNameOpts}
                        emptyOption={{ value: '', label: '— General marks —' }}
                        placeholder="Link to exam…"
                      />
                    </div>
                  ) : null}
                </div>

                {!marksEntry.className ? (
                  <p className="mps-msg mps-msg-info" style={{ marginTop: 16 }}>
                    Select a class to load students and subjects.
                  </p>
                ) : null}

                {marksEntry.className && !marksEntry.studentId ? (
                  <p className="mps-msg mps-msg-info" style={{ marginTop: 16 }}>
                    Select a student to enter marks for all subjects.
                  </p>
                ) : null}

                {marksEntry.studentId && classSubjects.length > 0 ? (
                  <div className="mps-marks-subject-grid" style={{ marginTop: 16 }}>
                    {classSubjects.map((sub) => {
                      const ass = marksEntry.assessmentName
                        ? findAssessment(assessments, marksEntry.className, marksEntry.assessmentName, sub.name)
                        : null;
                      return (
                        <div key={sub.id || sub.name} className="mps-field">
                          <label>
                            {sub.name}
                            {ass ? (
                              <span className="mps-muted" style={{ fontWeight: 'normal' }}>
                                {' '}
                                / {ass.maxMarks}
                              </span>
                            ) : null}
                          </label>
                          <input
                            type="number"
                            min="0"
                            max={ass?.maxMarks || undefined}
                            value={subjectScores[sub.name] ?? ''}
                            onChange={(e) =>
                              setSubjectScores((prev) => ({ ...prev, [sub.name]: e.target.value }))
                            }
                            placeholder="Score"
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {marksEntry.studentId && marksEntry.className && classSubjects.length === 0 ? (
                  <p className="mps-msg mps-msg-info" style={{ marginTop: 16 }}>
                    No subjects for class {formatClassLabel(marksEntry.className)}. Ask admin to add subjects in Academics.
                  </p>
                ) : null}

                <button
                  type="submit"
                  className="mps-btn"
                  style={{ marginTop: 16 }}
                  disabled={!marksEntry.studentId || savingMarks || classSubjects.length === 0}
                >
                  {savingMarks ? 'Saving…' : 'Save marks'}
                </button>
              </form>
            </div>
            <div className="mps-card">
              <h2>All marks</h2>
              <input
                type="search"
                className="mps-table-filter"
                value={marksTableFilter}
                onChange={(e) => setMarksTableFilter(e.target.value)}
                placeholder="Search student, class, subject, score…"
                aria-label="Filter marks table"
              />
              <table className="mps-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Class</th>
                    <th>Subject</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMarksRows.map((m) => (
                    <tr key={m.id}>
                      <td>
                        {m.studentName} ({m.studentId || m.id})
                      </td>
                      <td>{formatClassLabel(m.class)}</td>
                      <td>{m.subject}</td>
                      <td>{m.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}
